import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import Overlay from "../overlay";

import types from "./internal/types";
import renderInPortalWithGroup from "./internal/utils/renderInPortalWithGroup";
import {
  FillScreen,
  WIDTHS,
  PositionerAbsolute,
  PositionerRelative,
  ModalDialog
} from "./internal/styled/Modal";
import { Fade, SlideUp } from "./internal/AnimatedTransition";
import ModalContent from "./internal/ModalContent";

const FadeFullScreen = props => <Fade component={FillScreen} {...props} />;
const Positioner = ({ scrollBehavior, ...props }) => {
  const positionerComponent =
    scrollBehavior === "inside" ? PositionerAbsolute : PositionerRelative;

  return <SlideUp component={positionerComponent} {...props} />;
};

Positioner.propTypes = {
  scrollBehavior: PropTypes.oneOf(["inside", "outside"]).isRequired
};

const getScrollDistance = () =>
  window.pageYOffset ||
  document.documentElement.scrollTop ||
  document.body.scrollTop ||
  0;

export class Modal extends PureComponent {
  static propTypes = {
    /** Buttons to render in the footer. */
    actions: types.actions,
    /**
      What type of appearance the dialog should present itself in.
      determines the icon + it's color and the main action's color.
    */
    appearance: types.appearance,
    /**
      Boolean OR Function indicating which element to focus when the component mounts.
      TRUE will automatically find the first "tabbable" element within the modal.
      Providing a function should return the element you want to focus.
    */
    autoFocus: types.autoFocus,
    /** Content of the modal. */
    children: PropTypes.node,
    /**
      An icon to optionally display in the header before the heading. This will only have an effect in the case where
      the modal's appearance is `primary`.
      */
    headerIcon: PropTypes.node,
    /** The heading text. */
    heading: PropTypes.string,
    /** The desired fixed height of the modal. */
    height: PropTypes.number,
    /**
      The width of the modal. Takes multiple different forms including a string representing the desired width,
      or a straight unit as either a number or string.
    */
    width: types.width,
    /** Called when modal begins to close. */
    onClose: PropTypes.func,
    /** Called when the modal has finished closing - including it's closing animation. */
    onCloseComplete: PropTypes.func,
    /** Called when the modal has finished opening including it's animation. */
    onOpenComplete: PropTypes.func,
    /** Whether escape can be used to close the modal. */
    shouldCloseOnEscapePress: PropTypes.bool,
    /** Whether or not the modal should close when the overlay is clicked. */
    shouldCloseOnOverlayClick: PropTypes.bool,
    /**
      Where scroll behaviour should originate. When `inside` scroll only occurs
      on the modal body. When `outside` the entire modal will scroll within the viewport.
    */
    scrollBehavior: PropTypes.oneOf(["inside", "outside"]),

    /**
     * Custom class to use on modal, applied to dialog. Useful when wrapping modal
     * with 'styled()'
     */
    className: PropTypes.string
  };

  static defaultProps = {
    actions: [],
    autoFocus: true,
    children: null,
    heading: null,
    headerIcon: null,
    height: null,
    width: "md",
    onClose: () => {},
    onCloseComplete: () => {},
    onOpenComplete: () => {},
    shouldCloseOnEscapePress: true,
    shouldCloseOnOverlayClick: true,
    scrollBehavior: "inside",
    appearance: "primary",
    className: ""
  };

  constructor(props) {
    super(props);

    this.state = {
      isExiting: false,
      scrollDistance: getScrollDistance()
    };

    this.onDialogExit = this.onDialogExit.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
  }

  onDialogExit() {
    this.setState({ isExiting: true });
  }

  onOverlayClick() {
    if (this.props.shouldCloseOnOverlayClick) {
      this.props.onClose();
    }
  }

  onDialogClick = event => {
    // Prevent the click on the dialog from propogating to the Blanket.
    event.stopPropagation();
  };

  render() {
    const {
      // NOTE: `in` is provided by react-transition group and is not to be provided via a consumer of Modal.
      in: transitionIn, // eslint-disable-line react/prop-types
      actions,
      appearance,
      children,
      height,
      onCloseComplete,
      onOpenComplete,
      headerIcon,
      heading,
      width,
      scrollBehavior,
      className
    } = this.props;

    const transitionProps = { in: transitionIn };
    const widthName = WIDTHS.values.indexOf(width) > -1 ? width : null;
    const widthUnit = widthName ? WIDTHS.widths[widthName] : width;

    return (
      <FadeFullScreen
        {...transitionProps}
        onExit={this.onDialogExit}
        scrollDistance={this.state.scrollDistance}
      >
        <Overlay isTinted onOverlayClicked={this.onOverlayClick} />
        <Positioner
          {...transitionProps}
          onClick={this.onOverlayClick}
          onEntered={onOpenComplete}
          onExited={onCloseComplete}
          scrollBehavior={scrollBehavior}
          width={widthUnit}
        >
          <ModalDialog
            heightValue={height}
            onClick={this.onDialogClick}
            role="dialog"
            tabIndex="-1"
            width={widthUnit}
            className={className}
          >
            <ModalContent
              actions={actions}
              appearance={appearance}
              headerIcon={appearance === "primary" ? headerIcon : null}
              heading={heading}
              shouldScroll={scrollBehavior === "inside"}
            >
              {children}
            </ModalContent>
          </ModalDialog>
        </Positioner>
      </FadeFullScreen>
    );
  }
}

/* To provide the `in` prop to the Modal it needs to be wrappped in a TransitionGroup (via react-transition-group)
   to get the necessary lifecycle we also need to put it into a component which will then trigger the group to unmount,
   but still allow the animation before it is fully unmounted (in this case a portal is used).
   When this happens the `in` property will be updated as the state progresses, meaning that the animation will always
   trigger on both mount and unmount. */
export default renderInPortalWithGroup(Modal);
